// =============================================================================
// Cadastros — plano de contas e centros de custo, como estão no sistema
//
// Tela de CONSULTA, de propósito. Os dois vêm do Conta Azul, que é a fonte
// oficial (§3.1): um CC criado aqui não receberia lançamento nenhum, e um
// apagado aqui voltaria na próxima carga. Criar, renomear e desativar é lá; aqui
// se vê o que existe e se atualiza — sozinho toda madrugada, ou na hora pelo
// botão.
//
// A única decisão tomada aqui (migration 039): tirar uma categoria do controle
// orçamentário — IOF, tarifas, dividendos. Isso não vem do Conta Azul; é o
// Financeiro dizendo o que o orçamento controla.
// =============================================================================

import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, FalhaApi } from "../lib/api.ts";
import { Painel, Pilula, Vazio } from "../componentes/ui.tsx";
import { Modal } from "../componentes/Modal.tsx";

type Categoria = {
  id: string; codigo: string | null; nome: string; natureza: string; ativo: boolean;
  do_conta_azul: boolean; recebe_orcamento: boolean; fora_do_orcamento: boolean;
};
type Centro = { id: string; codigo: string; nome: string; ativo: boolean; do_conta_azul: boolean };
type Resposta = {
  categorias: Categoria[];
  centros: Centro[];
  ultima_atualizacao: { momento: string; modo: string | null } | null;
};
type Relatorio = {
  categorias: {
    recebidas: number;
    novas: { codigo: string | null; nome: string }[];
    renomeadas: { codigo: string | null; antes: string; depois: string }[];
    sumiram: { codigo: string | null; nome: string }[];
    natureza_diverge: { codigo: string | null; nome: string; conta_azul: string; aqui: string }[];
  };
  centros: {
    recebidos: number;
    novos: { codigo: string; nome: string }[];
    renomeados: { codigo: string; antes: string; depois: string }[];
    sumiram: { codigo: string; nome: string }[];
    ativo_diverge: { codigo: string; nome: string; conta_azul: boolean; aqui: boolean }[];
    codigo_em_uso: { codigo: string; nome: string }[];
  };
};

const DATA_HORA_SP = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", year: "numeric",
  hour: "2-digit", minute: "2-digit",
});
const NATUREZA: Record<string, string> = { despesa: "Despesa", receita: "Receita", investimento: "Investimento" };

export function Cadastros() {
  const qc = useQueryClient();
  const [aba, setAba] = useState<"centros" | "plano">("plano");
  const [busca, setBusca] = useState("");
  const [soOrcaveis, setSoOrcaveis] = useState(false);
  const [relatorio, setRelatorio] = useState<Relatorio | null>(null);
  const [decidindo, setDecidindo] = useState<Categoria | null>(null);

  const eu = useQuery({
    queryKey: ["eu"],
    queryFn: () => api.get<{ papeis: string[] }>("/auth/eu"),
    staleTime: 5 * 60_000,
  });
  const podeAtualizar = (eu.data?.papeis ?? []).some((p) => p === "financeiro" || p === "admin");

  const q = useQuery({ queryKey: ["cadastros-plano"], queryFn: () => api.get<Resposta>("/api/cadastros/plano") });

  const atualizar = useMutation({
    mutationFn: () => api.post<Relatorio>("/api/cadastros/atualizar", {}),
    onSuccess: async (r) => {
      setRelatorio(r);
      await qc.invalidateQueries();
    },
  });

  const termo = busca.trim().toLowerCase();
  const categorias = useMemo(() => (q.data?.categorias ?? []).filter((c) =>
    (!soOrcaveis || c.recebe_orcamento)
    && (!termo || `${c.codigo ?? ""} ${c.nome}`.toLowerCase().includes(termo))), [q.data, soOrcaveis, termo]);
  const centros = useMemo(() => (q.data?.centros ?? []).filter((c) =>
    !termo || `${c.codigo} ${c.nome}`.toLowerCase().includes(termo)), [q.data, termo]);

  const ultima = q.data?.ultima_atualizacao;

  return (
    <div className="pagina pb-20">
      <header className="flex flex-wrap items-end justify-between gap-4 py-7">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.11em] text-tinta-fraca uppercase">Cadastros</p>
          <h1 className="mt-1 font-display text-[22px] leading-tight font-bold tracking-tight">
            Plano de contas e centros de custo
          </h1>
          <p className="mt-0.5 max-w-2xl text-[13.5px] text-tinta-media">
            Vêm do Conta Azul, que é a fonte oficial: criar, renomear ou desativar é lá. Aqui eles se
            atualizam sozinhos toda madrugada — ou na hora, pelo botão.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {podeAtualizar && (
            <button type="button" disabled={atualizar.isPending} onClick={() => { setRelatorio(null); atualizar.mutate(); }}
              className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45">
              {atualizar.isPending ? "Atualizando…" : "Atualizar agora"}
            </button>
          )}
          <span className="text-[12px] text-tinta-fraca">
            {ultima
              ? <>Última atualização: <span className="num">{DATA_HORA_SP.format(new Date(ultima.momento))}</span>
                  {ultima.modo === "manual" ? " (pelo botão)" : " (automática)"}</>
              : "Ainda não houve atualização automática."}
          </span>
        </div>
      </header>

      {atualizar.isError && (
        <p className="mb-4 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[13px] text-risco-tinta">
          {(atualizar.error as FalhaApi).message}
        </p>
      )}

      {relatorio && <ResultadoAtualizacao r={relatorio} />}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Chip ativo={aba === "plano"} onClick={() => setAba("plano")}>Plano de contas · {q.data?.categorias.length ?? "…"}</Chip>
        <Chip ativo={aba === "centros"} onClick={() => setAba("centros")}>Centros de custo · {q.data?.centros.length ?? "…"}</Chip>
        {aba === "plano" && (
          <label className="ml-2 flex items-center gap-1.5 text-[12.5px] text-tinta-media">
            <input type="checkbox" checked={soOrcaveis} onChange={(e) => setSoOrcaveis(e.target.checked)} />
            só as que recebem orçamento
          </label>
        )}
        <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por código ou nome"
          aria-label="Buscar" className="ml-auto w-60 rounded-lg border border-linha bg-superficie px-2 py-1.5 text-[13px]" />
      </div>

      {q.isPending && <p className="text-[13px] text-tinta-fraca">Carregando…</p>}
      {q.isError && <Vazio titulo="Não foi possível carregar os cadastros" dica={(q.error as FalhaApi).message} />}

      {q.data && aba === "plano" && (
        categorias.length === 0 ? <Painel><Vazio titulo="Nenhuma categoria com esse filtro" /></Painel> : (
          <Tabela cabecalho={["Código", "Nome", "Natureza", "Recebe orçamento", "Situação"]}>
            {categorias.map((c) => {
              // A hierarquia está no próprio código (docs/PLANO_DE_CONTAS.md §2):
              // cada ponto é um nível.
              const nivel = c.codigo ? c.codigo.split(".").length - 1 : 0;
              return (
                <tr key={c.id} className="border-t border-linha">
                  <td className="num px-3 py-1.5 text-tinta-fraca">{c.codigo ?? "—"}</td>
                  <td className="px-3 py-1.5" style={{ paddingLeft: `${12 + nivel * 14}px` }}>
                    {c.nome}
                    {!c.do_conta_azul && <span className="ml-2 text-[11px] text-atencao-tinta">só no sistema</span>}
                  </td>
                  <td className="px-3 py-1.5 text-tinta-media">{NATUREZA[c.natureza] ?? c.natureza}</td>
                  <td className="px-3 py-1.5">
                    <span className="flex flex-wrap items-center gap-2">
                      {c.fora_do_orcamento ? <Pilula tom="neutro">Fora do orçamento</Pilula>
                        : c.recebe_orcamento ? <Pilula tom="ok">Sim</Pilula>
                        : <span className="text-tinta-fraca">—</span>}
                      {podeAtualizar && (c.recebe_orcamento || c.fora_do_orcamento) && (
                        <button type="button" onClick={() => setDecidindo(c)}
                          className="text-[11.5px] text-tinta-fraca hover:text-laranja-tinta hover:underline">
                          {c.fora_do_orcamento ? "voltar ao orçamento" : "tirar do orçamento"}
                        </button>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-1.5">{c.ativo ? "Ativa" : <Pilula tom="neutro">Inativa</Pilula>}</td>
                </tr>
              );
            })}
          </Tabela>
        )
      )}

      {decidindo && <ModalForaDoOrcamento categoria={decidindo} onFechar={() => setDecidindo(null)} />}

      {q.data && aba === "centros" && (
        centros.length === 0 ? <Painel><Vazio titulo="Nenhum centro de custo com esse filtro" /></Painel> : (
          <Tabela cabecalho={["Código", "Nome", "Situação"]}>
            {centros.map((c) => (
              <tr key={c.id} className="border-t border-linha">
                <td className="num px-3 py-1.5 text-tinta-fraca">{c.codigo}</td>
                <td className="px-3 py-1.5">
                  {c.nome}
                  {!c.do_conta_azul && <span className="ml-2 text-[11px] text-atencao-tinta">só no sistema — não recebe lançamento</span>}
                </td>
                <td className="px-3 py-1.5">{c.ativo ? "Ativo" : <Pilula tom="neutro">Inativo</Pilula>}</td>
              </tr>
            ))}
          </Tabela>
        )
      )}
    </div>
  );
}

function ResultadoAtualizacao({ r }: { r: Relatorio }) {
  const c = r.categorias, cc = r.centros;
  const nada = c.novas.length + c.renomeadas.length + cc.novos.length + cc.renomeados.length === 0;
  const alertas = c.sumiram.length + c.natureza_diverge.length + cc.sumiram.length
    + cc.ativo_diverge.length + cc.codigo_em_uso.length;
  const lista = (xs: string[]) => xs.slice(0, 6).join(" · ") + (xs.length > 6 ? ` · e mais ${xs.length - 6}` : "");
  return (
    <div className={`mb-4 rounded-lg border px-4 py-3 text-[13px] ${alertas ? "border-atencao-linha bg-atencao-bg" : "border-ok-linha bg-ok-bg"}`}>
      <p className="font-semibold">
        Atualizado: {c.recebidas} categorias e {cc.recebidos} centros de custo lidos do Conta Azul.
        {nada && " Nada mudou."}
      </p>
      <ul className="mt-1.5 grid gap-1 text-tinta-media">
        {c.novas.length > 0 && <li>{c.novas.length} categoria(s) nova(s): {lista(c.novas.map((x) => `${x.codigo ?? ""} ${x.nome}`.trim()))}</li>}
        {c.renomeadas.length > 0 && <li>{c.renomeadas.length} renomeada(s): {lista(c.renomeadas.map((x) => `${x.antes} → ${x.depois}`))}</li>}
        {cc.novos.length > 0 && <li>{cc.novos.length} centro(s) de custo novo(s): {lista(cc.novos.map((x) => `${x.codigo} ${x.nome}`))}</li>}
        {cc.renomeados.length > 0 && <li>{cc.renomeados.length} CC renomeado(s): {lista(cc.renomeados.map((x) => `${x.antes} → ${x.depois}`))}</li>}
        {c.sumiram.length > 0 && <li>⚠️ {c.sumiram.length} categoria(s) não vieram mais do Conta Azul — continuam aqui, nada foi apagado: {lista(c.sumiram.map((x) => x.nome))}</li>}
        {cc.sumiram.length > 0 && <li>⚠️ {cc.sumiram.length} CC(s) não vieram mais do Conta Azul — continuam aqui: {lista(cc.sumiram.map((x) => `${x.codigo} ${x.nome}`))}</li>}
        {c.natureza_diverge.length > 0 && <li>⚠️ {c.natureza_diverge.length} categoria(s) com natureza diferente da do Conta Azul — vale a daqui: {lista(c.natureza_diverge.map((x) => `${x.nome} (lá ${x.conta_azul}, aqui ${x.aqui})`))}</li>}
        {cc.ativo_diverge.length > 0 && <li>⚠️ {cc.ativo_diverge.length} CC(s) ativo num lado e inativo no outro — vale o daqui: {lista(cc.ativo_diverge.map((x) => `${x.codigo} ${x.nome}`))}</li>}
        {cc.codigo_em_uso.length > 0 && <li>⚠️ {cc.codigo_em_uso.length} código(s) de CC do Conta Azul já usados aqui por outro centro: {lista(cc.codigo_em_uso.map((x) => `${x.codigo} ${x.nome}`))}</li>}
      </ul>
    </div>
  );
}

function Tabela({ cabecalho, children }: { cabecalho: string[]; children: ReactNode }) {
  return (
    <div className="thin-scroll overflow-x-auto rounded-lg border border-linha bg-superficie">
      <table className="w-full border-collapse text-[13px]">
        <thead className="bg-superficie-2">
          <tr className="text-left text-[11px] font-semibold tracking-wide text-tinta-fraca uppercase">
            {cabecalho.map((h) => <th key={h} className="px-3 py-2">{h}</th>)}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Chip({ ativo, onClick, children }: { ativo: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" aria-pressed={ativo} onClick={onClick}
      className={`rounded-lg border px-3 py-1 text-[12.5px] font-semibold ${
        ativo ? "border-azul bg-azul text-white" : "border-linha bg-superficie text-tinta-media hover:bg-superficie-2"
      }`}>
      {children}
    </button>
  );
}

/**
 * Tirar a categoria do controle orçamentário, ou devolver. Motivo obrigatório:
 * fica no Histórico, e é o que explica por que um gasto deixou de cobrar orçamento.
 */
function ModalForaDoOrcamento({ categoria: c, onFechar }: { categoria: Categoria; onFechar: () => void }) {
  const qc = useQueryClient();
  const [motivo, setMotivo] = useState("");
  const tirar = !c.fora_do_orcamento;
  const salvar = useMutation({
    mutationFn: () => api.post<{ pendencias: number }>("/api/cadastros/categorias/fora-do-orcamento", {
      categoria_ids: [c.id], fora: tirar, motivo: motivo.trim(),
    }),
    onSuccess: async () => {
      await qc.invalidateQueries();
      onFechar();
    },
  });
  const nome = `${c.codigo ? c.codigo + " " : ""}${c.nome}`;
  return (
    <Modal titulo={tirar ? "Tirar do controle orçamentário" : "Voltar ao controle orçamentário"} onFechar={onFechar}>
      <p className="text-[14px] font-semibold">{nome}</p>
      {tirar ? (
        <ul className="mt-2 grid list-disc gap-1 pl-5 text-[13px] text-tinta-media">
          <li>Os lançamentos dela deixam de gerar &quot;não orçado&quot; e &quot;sem centro de custo&quot;, e as pendências abertas saem da fila.</li>
          <li>Ela deixa de ser oferecida para orçamento.</li>
          <li>O gasto continua no Realizado: só deixa de cobrar orçamento.</li>
        </ul>
      ) : (
        <p className="mt-2 text-[13px] text-tinta-media">
          Ela volta a ser orçável, e as pendências que a marcação tinha fechado voltam para a fila — só as que ainda
          têm causa.
        </p>
      )}
      <label className="mt-3 block">
        <span className="text-[12px] font-semibold text-tinta-media">Motivo <span className="text-laranja-tinta">*</span></span>
        <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3}
          placeholder={tirar ? "Ex.: decidido com a Mariana em 22/09 — tarifa bancária não é controlada por orçamento." : "Por que volta."}
          className="mt-1 w-full resize-y rounded border border-linha bg-superficie px-3 py-2 text-[14px]" />
        <span className="mt-1 block text-[11.5px] text-tinta-fraca">Fica no Histórico de Movimentações.</span>
      </label>
      {salvar.isError && (
        <p className="mt-3 rounded border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
          {(salvar.error as FalhaApi).message}
        </p>
      )}
      <div className="mt-5 flex justify-end gap-2">
        <button type="button" onClick={onFechar}
          className="rounded border border-linha px-4 py-2 text-[13px] font-semibold hover:bg-superficie-2">Cancelar</button>
        <button type="button" disabled={motivo.trim().length < 5 || salvar.isPending} onClick={() => salvar.mutate()}
          className="rounded bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45">
          {salvar.isPending ? "Registrando…" : tirar ? "Tirar do orçamento" : "Voltar ao orçamento"}
        </button>
      </div>
    </Modal>
  );
}
